import { Injectable, inject } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse } from '@angular/common/http';
import { Observable, from, throwError } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import { MsalService } from '@azure/msal-angular';
import { AuthenticationResult } from '@azure/msal-browser';
import { AuthService } from '../services/auth.service';
import { environment } from '../../../environments/environment';

/**
 * HTTP Interceptor that conditionally requests Metrics.Write scope for admin endpoints
 * based on whether the user has the DashboardAdmin role.
 * 
 * This interceptor runs before MsalInterceptor and handles admin API requests differently:
 * - Checks if the request is for an admin endpoint (/admin/*)
 * - Verifies user has admin access (DashboardAdmin role)
 * - Acquires token with Metrics.Write scope
 * - Attaches the token to the request
 * 
 * For non-admin endpoints, this interceptor passes the request through to MsalInterceptor.
 */
@Injectable()
export class AdminScopeInterceptor implements HttpInterceptor {
  private readonly msalService = inject(MsalService);
  private readonly authService = inject(AuthService);

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Skip in no-auth mode - MockJwtInterceptor handles it
    if (environment.noAuthMode) {
      return next.handle(req);
    }

    // Check if this is an admin endpoint
    if (!this.isAdminEndpoint(req.url)) {
      // Not an admin endpoint - let MsalInterceptor handle with default Read scope
      return next.handle(req);
    }

    // Admin endpoint detected - check if user has admin access
    if (!this.authService.hasAdminAccess()) {
      // User lacks admin role - deny immediately
      console.error('AdminScopeInterceptor: User lacks DashboardAdmin role for admin endpoint', {
        url: req.url,
        method: req.method
      });
      return throwError(() => new HttpErrorResponse({
        error: 'Forbidden: Admin access required',
        status: 403,
        statusText: 'Forbidden',
        url: req.url
      }));
    }

    // User has admin access - acquire token with Write scope
    return from(this.acquireAdminToken()).pipe(
      switchMap((token) => {
        if (!token) {
          console.error('AdminScopeInterceptor: Failed to acquire admin token');
          return throwError(() => new HttpErrorResponse({
            error: 'Failed to acquire admin access token',
            status: 401,
            statusText: 'Unauthorized',
            url: req.url
          }));
        }

        // Clone request with admin token
        const authReq = req.clone({
          setHeaders: {
            Authorization: `Bearer ${token}`
          }
        });

        console.debug('AdminScopeInterceptor: Attached admin token with Metrics.Write scope', {
          url: req.url,
          method: req.method
        });

        return next.handle(authReq);
      }),
      catchError((error) => {
        console.error('AdminScopeInterceptor: Error acquiring admin token', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Check if the request URL is for an admin endpoint
   */
  private isAdminEndpoint(url: string): boolean {
    // Match paths containing /admin/ (strict check for admin routes)
    return url.includes('/admin/') || url.includes('/v1/admin');
  }

  /**
   * Acquire access token with Metrics.Write scope
   */
  private async acquireAdminToken(): Promise<string | null> {
    const account = this.authService.getAccount();
    if (!account) {
      console.warn('AdminScopeInterceptor: No account available for token acquisition');
      return null;
    }

    try {
      const result: AuthenticationResult = await this.msalService.instance.acquireTokenSilent({
        scopes: environment.msal.scopes.write,
        account: account
      });

      console.debug('AdminScopeInterceptor: Successfully acquired admin token', {
        scopes: result.scopes,
        expiresOn: result.expiresOn
      });

      return result.accessToken;
    } catch (error) {
      console.error('AdminScopeInterceptor: Failed to acquire token silently', error);
      
      // If silent acquisition fails, could trigger interactive login
      // For now, return null and let error handling propagate
      // In production, consider: await this.msalService.acquireTokenPopup({ scopes: environment.msal.scopes.write });
      
      return null;
    }
  }
}
